import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Edit2, Trash2, Tag, Percent, DollarSign, Gift } from 'lucide-react';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import promotionsApi from 'api/requests/promotions';
import ConfirmDialog from 'modules/restaurant/menu/components/ConfirmDialog';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

const TYPE_LABELS = {
    percentage: 'Percentual',
    fixed:       'Valor fixo',
    fixed_price: 'Preço fixo',
    bundle:      'Combo',
};

const TYPE_COLORS = {
    percentage:  { bg: `${panelAccentAlpha(colors.occasionalPurple, '14')}`, color: panelAccent(colors.occasionalPurple) },
    fixed:       { bg: `${colors.primary}14`,           color: colors.primary },
    fixed_price: { bg: `${colors.occasionalGreen}14`,   color: colors.occasionalGreen },
    bundle:      { bg: `${colors.occasionalOrange}14`,  color: colors.occasionalOrange },
};

const TYPE_ICONS = {
    percentage:  Percent,
    fixed:       DollarSign,
    fixed_price: Tag,
    bundle:      Gift,
};

const DAY_NAMES = ['', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function formatSchedule(schedule) {
    if (!schedule) return '—';
    if (schedule.schedule_type === 'one_time') {
        const start = schedule.starts_at
            ? new Date(schedule.starts_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
            : '';
        const startTime = schedule.starts_at
            ? new Date(schedule.starts_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
            : '';
        const endTime = schedule.ends_at
            ? new Date(schedule.ends_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
            : '';
        return `${start} · ${startTime}–${endTime}`;
    }
    const days = (schedule.days_of_week || []).sort();
    const dayStr = compressDays(days);
    const t1 = (schedule.time_start || '').slice(0, 5);
    const t2 = (schedule.time_end || '').slice(0, 5);
    return `${dayStr} · ${t1}–${t2}`;
}

function compressDays(days) {
    if (!days.length) return '—';
    const ranges = [];
    let start = days[0], end = days[0];
    for (let i = 1; i < days.length; i++) {
        if (days[i] === end + 1) {
            end = days[i];
        } else {
            ranges.push(start === end ? DAY_NAMES[start] : `${DAY_NAMES[start]}–${DAY_NAMES[end]}`);
            start = end = days[i];
        }
    }
    ranges.push(start === end ? DAY_NAMES[start] : `${DAY_NAMES[start]}–${DAY_NAMES[end]}`);
    return ranges.join(', ');
}


function formatUses(promotion) {
    const uses = promotion.uses_count ?? 0;
    if (promotion.max_uses == null) return `${uses} / ∞`;
    return `${uses} / ${promotion.max_uses}`;
}

export default function PromotionsList({ promotions, slug, onEdit, onDeleted, onToggleActive, toast }) {
    const [confirm, setConfirm] = useState({ open: false, id: null, name: '' });
    const [deleting, setDeleting] = useState(false);
    const [togglingId, setTogglingId] = useState(null);

    async function handleConfirmDelete() {
        setDeleting(true);
        try {
            await promotionsApi.remove(slug, confirm.id);
            onDeleted(confirm.id);
            toast?.success('Promoção removida');
        } catch {
            toast?.error('Erro ao remover promoção');
        } finally {
            setDeleting(false);
            setConfirm({ open: false, id: null, name: '' });
        }
    }

    async function handleToggle(promotion) {
        if (togglingId) return;
        setTogglingId(promotion.id);
        const updated = { ...promotion, is_active: !promotion.is_active };
        try {
            const payload = buildTogglePayload(promotion);
            await promotionsApi.update(slug, promotion.id, payload);
            onToggleActive(promotion.id, !promotion.is_active);
        } catch {
            toast?.error('Erro ao atualizar promoção');
        } finally {
            setTogglingId(null);
        }
    }

    function buildTogglePayload(promo) {
        const s = promo.schedule || {};
        const schedulePayload = s.schedule_type === 'one_time'
            ? { schedule_type: 'one_time', starts_at: s.starts_at, ends_at: s.ends_at }
            : {
                schedule_type: 'recurring',
                days_of_week: s.days_of_week || [],
                time_start: s.time_start,
                time_end: s.time_end,
                valid_from: s.valid_from || null,
                valid_until: s.valid_until || null,
            };

        const payload = {
            name: promo.name,
            type: promo.type,
            is_active: !promo.is_active,
            max_uses: promo.max_uses ?? null,
            schedule: schedulePayload,
        };

        if (promo.type === 'bundle') {
            payload.buy_menu_product_id = promo.buy_menu_product_id;
            payload.buy_quantity = promo.buy_quantity;
            payload.get_menu_product_id = promo.get_menu_product_id;
            payload.get_quantity = promo.get_quantity;
        } else {
            payload.discount_value = promo.discount_value;
            payload.product_ids = (promo.products || []).map(p => p.id);
        }

        return payload;
    }

    if (promotions.length === 0) {
        return (
            <EmptyState>
                <EmptyIcon><Tag size={28} color={panelAccent(colors.occasionalPurple)} /></EmptyIcon>
                <EmptyTitle>Nenhuma promoção cadastrada</EmptyTitle>
                <EmptyHint>Crie promoções para aplicar descontos automaticamente no PDV</EmptyHint>
            </EmptyState>
        );
    }

    return (
        <>
            <TableWrapper>
                <Table>
                    <thead>
                        <tr>
                            <Th>Nome</Th>
                            <Th $hide='sm'>Tipo</Th>
                            <Th $hide='md'>Agendamento</Th>
                            <Th $hide='sm'>Usos</Th>
                            <Th>Ativo</Th>
                            <Th $right>Ações</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {promotions.map(promo => {
                            const typeCfg = TYPE_COLORS[promo.type] || TYPE_COLORS.percentage;
                            const TypeIcon = TYPE_ICONS[promo.type] || Percent;
                            const isToggling = togglingId === promo.id;

                            return (
                                <Tr key={promo.id}>
                                    <Td>
                                        <PromoName>{promo.name}</PromoName>
                                    </Td>

                                    <Td $hide='sm'>
                                        <TypeBadge $bg={typeCfg.bg} $color={typeCfg.color}>
                                            <TypeIcon size={11} />
                                            {TYPE_LABELS[promo.type] || promo.type}
                                        </TypeBadge>
                                    </Td>

                                    <Td $hide='md'>
                                        <ScheduleText>{formatSchedule(promo.schedule)}</ScheduleText>
                                    </Td>

                                    <Td $hide='sm'>
                                        <UsesText>{formatUses(promo)}</UsesText>
                                    </Td>

                                    <Td>
                                        <ToggleSwitch
                                            $on={promo.is_active}
                                            $loading={isToggling}
                                            onClick={() => !isToggling && handleToggle(promo)}
                                            title={promo.is_active ? 'Desativar' : 'Ativar'}
                                        >
                                            <ToggleKnob $on={promo.is_active} />
                                        </ToggleSwitch>
                                    </Td>

                                    <Td $right>
                                        <Actions>
                                            <ActionBtn
                                                onClick={() => onEdit(promo)}
                                                title='Editar'
                                            >
                                                <Edit2 size={14} color={colors.night} />
                                            </ActionBtn>
                                            <ActionBtn
                                                onClick={() => setConfirm({ open: true, id: promo.id, name: promo.name })}
                                                title='Remover'
                                            >
                                                <Trash2 size={14} color={colors.danger} />
                                            </ActionBtn>
                                        </Actions>
                                    </Td>
                                </Tr>
                            );
                        })}
                    </tbody>
                </Table>
            </TableWrapper>

            <ConfirmDialog
                isOpen={confirm.open}
                title='Remover promoção'
                message={`Tem certeza que deseja remover "${confirm.name}"? Esta ação não pode ser desfeita.`}
                confirmLabel='Remover'
                danger
                loading={deleting}
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirm({ open: false, id: null, name: '' })}
            />
        </>
    );
}

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
`;

const EmptyIcon = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
`;

const EmptyTitle = styled.p`font-weight: 600; font-size: 16px; color: ${colors.nightDark}; margin: 0 0 6px;`;
const EmptyHint = styled.p`font-size: 13px; color: #666; margin: 0; max-width: 300px;`;

const TableWrapper = styled.div`
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const Th = styled.th`
    padding: 10px 16px;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    text-align: ${({ $right }) => $right ? 'right' : 'left'};
    border-bottom: 1px solid ${colors.neutralDark};
    white-space: nowrap;

    @media screen and (max-width: ${breakpoints.md}) {
        ${({ $hide }) => $hide === 'md' && 'display: none;'}
    }
    @media screen and (max-width: ${breakpoints.sm}) {
        ${({ $hide }) => ($hide === 'sm' || $hide === 'md') && 'display: none;'}
    }
`;

const Tr = styled.tr`
    border-bottom: 1px solid ${colors.neutralDark};
    transition: background 0.1s;
    &:last-child { border-bottom: none; }
    &:hover { background: ${colors.neutral}80; }
`;

const Td = styled.td`
    padding: 12px 16px;
    vertical-align: middle;
    text-align: ${({ $right }) => $right ? 'right' : 'left'};

    @media screen and (max-width: ${breakpoints.md}) {
        ${({ $hide }) => $hide === 'md' && 'display: none;'}
    }
    @media screen and (max-width: ${breakpoints.sm}) {
        ${({ $hide }) => ($hide === 'sm' || $hide === 'md') && 'display: none;'}
    }
`;

const PromoName = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const TypeBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    background: ${({ $bg }) => $bg};
    color: ${({ $color }) => $color};
    white-space: nowrap;
`;

const ScheduleText = styled.span`
    font-size: 12px;
    color: ${colors.night};
`;

const UsesText = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    font-variant-numeric: tabular-nums;
`;

const ToggleSwitch = styled.button`
    width: 38px;
    height: 22px;
    border-radius: 11px;
    border: none;
    cursor: ${({ $loading }) => $loading ? 'wait' : 'pointer'};
    background: ${({ $on }) => $on ? colors.occasionalGreen : colors.neutralDark};
    position: relative;
    flex-shrink: 0;
    transition: background 0.2s;
    padding: 0;
    opacity: ${({ $loading }) => $loading ? 0.7 : 1};
`;

const ToggleKnob = styled.div`
    position: absolute;
    top: 2px;
    left: ${({ $on }) => $on ? '18px' : '2px'};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    transition: left 0.2s;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
`;

const ActionBtn = styled.button`
    background: none;
    border: none;
    padding: 6px;
    cursor: pointer;
    border-radius: 6px;
    display: flex;
    align-items: center;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
